/**
 * 単一選択セレクトボックスを `Ajax` 経由で更新する関数
 * 重複更新を防ぐために更新中フラグを内部に保持したクロージャとして実装
 *
 * @param {*} element  イベントの発火要素
 * @param {string} selector  対象セレクトのセレクタ
 * @param {Object} data  Ajax 送信データ
 * @param {Object} [options]  コールバック用オプション
 * @param {Function} [options.onSuccess]  `Ajax` 成功時コールバック
 * @param {Function} [options.onError]  `Ajax` 失敗時コールバック
 * @param {Function} [options.onFinally]  成功・失敗に関わらず最後に呼ばれるコールバック
 */
const update = (() => {
    // 更新中フラグマップを初期化
    const updatingMap = new Map();

    return (element, selector, data = {}, {
        onSuccess = null,
        onError = null,
        onFinally = null,
        showEmptyOption = true,
    } = {}) => {
        if (updatingMap.get(selector)) {
            return;
        }

        const $targetSelect = findTargetSelect(element, selector);
        if ($targetSelect.length === 0) {
            return
        };

        const url = $targetSelect.data('action');
        const headers = { 'X-CSRF-TOKEN': getCsrfToken() };

        // 更新中フラグを設定（同一セレクトの重複更新を防止）
        updatingMap.set(selector, true);

        const ajaxOptions = {
            url,
            type: 'post',
            dataType: 'json',
            contentType: 'application/json',
            data: JSON.stringify(data),
            headers,
        };

        $.ajax(ajaxOptions).then((response) => {
            const options = response.options || [];

            $targetSelect.empty();
            options.forEach((option) => {
                if (option.id === null || option.id === undefined) {
                    return;
                }

                if (!showEmptyOption && option.id === '') {
                    return;
                }

                $targetSelect.append(
                    new Option(option.text, option.id, false, false)
                );
            });
            $targetSelect.trigger('change');

            // 成功時コールバックを実行
            onSuccess?.(response);
        }).catch((jqXHR, textStatus, errorThrown) => {
            // 共通エラーハンドラを実行
            _error.omitted(jqXHR, textStatus, errorThrown);

            // 失敗時コールバックを実行
            onError?.(jqXHR, textStatus, errorThrown);
        }).always(() => {
            // 更新中フラグを解除（同一セレクトの再更新を許可）
            updatingMap.delete(selector);

            // 最終処理コールバックを実行（成功・失敗問わず）
            onFinally?.();
        });
    };
})();

/**
 * `multipleSelect` プラグインを使用した複数選択セレクトボックスを `Ajax` 経由で更新する関数
 * 重複更新を防ぐために更新中フラグを内部に保持したクロージャとして実装
 *
 * @param {*} element  イベントの発火要素
 * @param {string} selector  対象セレクトのセレクタ
 * @param {Object} data  Ajax 送信データ
 * @param {Object} [options]  コールバック用オプション
 * @param {Function} [options.onSuccess]  `Ajax` 成功時コールバック
 * @param {Function} [options.onError]  `Ajax` 失敗時コールバック
 * @param {Function} [options.onFinally]  成功・失敗に関わらず最後に呼ばれるコールバック
 */
const updateMulti = (() => {
    // 更新中フラグマップを初期化
    const updatingMap = new Map();

    return (element, selector, data = {}, {
        onSuccess = null,
        onError = null,
        onFinally = null,
        showEmptyOption = true,
    } = {}) => {
        if (updatingMap.get(selector)) {
            return;
        }

        const $targetSelect = findTargetSelect(element, selector);
        if ($targetSelect.length === 0) {
            return
        };

        const url = $targetSelect.data('action');
        const headers = { 'X-CSRF-TOKEN': getCsrfToken() };

        // 更新中フラグを設定（同一セレクトの重複更新を防止）
        updatingMap.set(selector, true);

        const ajaxOptions = {
            url,
            type: 'post',
            dataType: 'json',
            contentType: 'application/json',
            data: JSON.stringify(data),
            headers,
        };

        $.ajax(ajaxOptions).then((response) => {
            const options = response.options || [];

            $targetSelect.empty();
            options.forEach((option) => {
                if (option.id === null || option.id === undefined) {
                    return;
                }

                if (!showEmptyOption && option.id === '') {
                    return;
                }

                $targetSelect.append(
                    new Option(option.text, option.id, false, false)
                );
            });

            $targetSelect.multipleSelect('refreshOptions', {
                filter: false,
            });

            // 成功時コールバックを実行
            onSuccess?.(response);
        }).catch((jqXHR, textStatus, errorThrown) => {
            // 共通エラーハンドラを実行
            _error.omitted(jqXHR, textStatus, errorThrown);

            // 失敗時コールバックを実行
            onError?.(jqXHR, textStatus, errorThrown);
        }).always(() => {
            // 更新中フラグを解除（同一セレクトの再更新を許可）
            updatingMap.delete(selector);

            // 最終処理コールバックを実行（成功・失敗問わず）
            onFinally?.();
        });
    };
})();

/**
 * 指定したセレクトをクリアしてデフォルトの `-----` を表示
 *
 * @param {*} element  イベントの発火要素
 * @param {string} selector  対象セレクトのセレクタ
 */
const clear = (element, selector) => {
    const $targetSelect = findTargetSelect(element, selector);
    if ($targetSelect.length === 0) {
        return;
    }

    $targetSelect.val(null).trigger('change');
    $targetSelect.empty().append(
        new Option('-----', '', true, true)
    ).trigger('change');
};

/**
 * 指定したマルチセレクトをクリア
 *
 * @param {*} element  イベントの発火要素
 * @param {string} selector  対象セレクトのセレクタ
 */
const clearMulti = (element, selector) => {
    const $targetSelect = findTargetSelect(element, selector);
    if ($targetSelect.length === 0) {
        return;
    }

    $targetSelect.val([]).empty().trigger('change');
    if ($targetSelect.data('multipleSelect')) {
        $targetSelect.multipleSelect('refresh');
    }
};

/**
 * セレクトボックスを有効化
 *
 * @param {*} element  イベントの発火要素
 * @param {string} selector  対象セレクトのセレクタ
 */
const enable = (element, selector) => {
    const $targetSelect = findTargetSelect(element, selector);
    if ($targetSelect.length === 0) { return; }
    $targetSelect.prop('disabled', false).trigger('change');
};

/**
 * セレクトボックスを無効化
 *
 * @param {*} element  イベントの発火要素
 * @param {string} selector  対象セレクトのセレクタ
 */
const disable = (element, selector) => {
    const $targetSelect = findTargetSelect(element, selector);
    if ($targetSelect.length === 0) { return; }
    $targetSelect.prop('disabled', true).trigger('change');
};

/**
 * イベントの発火要素から `form` を探索して、
 * その中から `selector` に一致する要素を返却
 *
 * @param {*} element  イベントの発火要素
 * @param {string} selector  対象セレクトのセレクタ
 * @returns {JQuery}  対象セレクト
 */
const findTargetSelect = (element, selector) => {
    const $form = $(element).closest('form');
    return $form.find(selector);
};

/**
 * `meta` タグから `CSRFトークン` を取得
 *
 * @returns {string}  CSRFトークン
 */
const getCsrfToken = () => {
    return $('meta[name="csrf-token"]').attr('content');
};

/**
 * セレクトボックス関連の関数群をまとめた名前空間オブジェクト
 */
export const SelectHelper = {
    update,
    updateMulti,
    clear,
    clearMulti,
    enable,
    disable,
};
